/**
 * Project Map Thumbnail Component
 * Renders a mini static map preview for project cards
 */

import { useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker } from 'react-leaflet';
import { MapPin } from 'lucide-react';
import type { BoundingBox } from '../../db';
import type { Inspection } from '../../types';
import { useThemeStore } from '../../store/themeStore';
import 'leaflet/dist/leaflet.css';

interface ProjectMapThumbnailProps {
  boundingBox?: BoundingBox;
  inspectionData?: Inspection;
  className?: string;
}

// Extract coordinates from inspection data
function getCoordinatesFromInspection(inspection: Inspection): [number, number][] {
  const coords: [number, number][] = [];
  
  // Get coordinates from all element types
  const elementTypes = ['stations', 'bays', 'segments', 'supports'] as const;
  
  for (const type of elementTypes) {
    const elements = inspection[type];
    if (Array.isArray(elements)) {
      for (const element of elements) {
        if (element.coordinate && Array.isArray(element.coordinate)) {
          for (const coord of element.coordinate) {
            if (coord && typeof coord.latitude === 'number' && typeof coord.longitude === 'number') {
              coords.push([coord.latitude, coord.longitude]);
            }
          }
        }
      }
    }
  }
  
  return coords;
}

// Calculate bounds from coordinates
function calculateBounds(coords: [number, number][]): BoundingBox | null {
  if (coords.length === 0) return null;
  
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;
  
  for (const [lat, lon] of coords) {
    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lon > east) east = lon;
    if (lon < west) west = lon;
  }
  
  return { north, south, east, west };
}

export function ProjectMapThumbnail({ boundingBox, inspectionData, className = '' }: ProjectMapThumbnailProps) {
  const { mode } = useThemeStore();
  
  // Determine if dark mode is active
  const isDark = useMemo(() => {
    if (mode === 'dark') return true;
    if (mode === 'light') return false;
    // System mode - check document class
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return true; // Default to dark
  }, [mode]);
  
  // Calculate bounds and line coordinates
  const { bounds, lineCoords } = useMemo(() => {
    let coords: [number, number][] = [];
    let calcBounds = boundingBox;
    
    if (inspectionData) {
      coords = getCoordinatesFromInspection(inspectionData);
      if (!calcBounds && coords.length > 0) {
        calcBounds = calculateBounds(coords);
      }
    }
    
    return {
      bounds: calcBounds,
      lineCoords: coords
    };
  }, [boundingBox, inspectionData]);
  
  // If no bounds available, show placeholder
  if (!bounds) {
    return (
      <div className={`bg-slate-200 dark:bg-slate-700/50 rounded-lg flex items-center justify-center border border-slate-300 dark:border-slate-600/50 ${className}`}>
        <MapPin className="w-10 h-10 text-slate-400 dark:text-slate-500" />
      </div>
    );
  }
  
  // Calculate center and leaflet bounds
  const center: [number, number] = [
    (bounds.north + bounds.south) / 2,
    (bounds.east + bounds.west) / 2
  ];
  
  const leafletBounds: [[number, number], [number, number]] = [
    [bounds.south, bounds.west],
    [bounds.north, bounds.east]
  ];
  
  // Choose tile layer based on theme
  const tileUrl = isDark
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
  
  return (
    <div className={`rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600/50 ${className}`}>
      <MapContainer
        bounds={leafletBounds}
        boundsOptions={{ padding: [10, 10] }}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ width: '100%', height: '100%' }}
        className="bg-slate-200 dark:bg-slate-800"
      >
        <TileLayer
          url={tileUrl}
          key={tileUrl} // Force re-render when URL changes
        />
        
        {/* Draw line through all coordinates */}
        {lineCoords.length > 1 && (
          <Polyline
            positions={lineCoords}
            pathOptions={{
              color: '#3b82f6',
              weight: 2,
              opacity: 0.8
            }}
          />
        )}
        
        {/* Show center point if only one coordinate */}
        {lineCoords.length === 1 && (
          <CircleMarker
            center={lineCoords[0]}
            radius={6}
            pathOptions={{
              fillColor: '#3b82f6',
              fillOpacity: 1,
              color: '#1d4ed8',
              weight: 2
            }}
          />
        )}
        
        {/* Show start and end points */}
        {lineCoords.length > 1 && (
          <>
            <CircleMarker
              center={lineCoords[0]}
              radius={4}
              pathOptions={{
                fillColor: '#22c55e',
                fillOpacity: 1,
                color: '#16a34a',
                weight: 2
              }}
            />
            <CircleMarker
              center={lineCoords[lineCoords.length - 1]}
              radius={4}
              pathOptions={{
                fillColor: '#ef4444',
                fillOpacity: 1,
                color: '#dc2626',
                weight: 2
              }}
            />
          </>
        )}
      </MapContainer>
      
      {/* Grid overlay for aviation style */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-20 dark:opacity-20"
        style={{
          backgroundImage: 
            'linear-gradient(rgba(100, 116, 139, 0.3) 1px, transparent 1px), ' +
            'linear-gradient(90deg, rgba(100, 116, 139, 0.3) 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      />
    </div>
  );
}
